import React, { useState, useEffect, useRef, useCallback } from 'react';

function BannerCarousel({ banners = [], autoPlayInterval = 4000 }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const intervalRef = useRef(null);
  const carouselRef = useRef(null);

  const activeBanners = banners.filter(b => b.b_ativo);

  const startAutoPlay = useCallback(() => {
    if (activeBanners.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % activeBanners.length);
    }, autoPlayInterval);
  }, [activeBanners.length, autoPlayInterval]);

  const stopAutoPlay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, [startAutoPlay, stopAutoPlay]);

  const goToSlide = (index) => {
    stopAutoPlay();
    setCurrentIndex(index);
    startAutoPlay();
  };

  const goNext = () => {
    stopAutoPlay();
    setCurrentIndex(prev => (prev + 1) % activeBanners.length);
    startAutoPlay();
  };

  const goPrev = () => {
    stopAutoPlay();
    setCurrentIndex(prev => (prev - 1 + activeBanners.length) % activeBanners.length);
    startAutoPlay();
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
    setIsDragging(true);
    stopAutoPlay();
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    const diff = touchStartX - touchEndX;
    const minSwipe = 50;

    if (Math.abs(diff) > minSwipe) {
      if (diff > 0) {
        goNext();
      } else {
        goPrev();
      }
    } else {
      startAutoPlay();
    }
  };

  const handleBannerClick = (banner) => {
    if (banner.link_url) {
      window.open(banner.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!activeBanners || activeBanners.length === 0) {
    return null;
  }

  const getImageUrl = (banner) => {
    if (banner.imagem?.imagem) {
      return `https://grubupload.s3.amazonaws.com/${banner.imagem.imagem}`;
    }
    if (banner.imagem_url) {
      return banner.imagem_url.startsWith('http')
        ? banner.imagem_url
        : `https://grubupload.s3.amazonaws.com/${banner.imagem_url}`;
    }
    return null;
  };

  return (
    <div
      ref={carouselRef}
      className="banner-carousel"
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides */}
      <div
        className="banner-carousel-track"
        style={{
          display: 'flex',
          transition: isDragging ? 'none' : 'transform 0.4s ease-in-out',
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {activeBanners.map((banner, index) => {
          const imgUrl = getImageUrl(banner);
          return (
            <div
              key={banner.id || index}
              className="banner-carousel-slide"
              style={{
                minWidth: '100%',
                cursor: banner.link_url ? 'pointer' : 'default',
              }}
              onClick={() => handleBannerClick(banner)}
            >
              <img
                src={imgUrl}
                alt={`Banner ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </div>
          );
        })}
      </div>

      {/* Indicadores (dots) */}
      {activeBanners.length > 1 && (
        <div
          className="banner-carousel-dots"
          style={{
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '6px',
            zIndex: 2,
          }}
        >
          {activeBanners.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                goToSlide(index);
              }}
              aria-label={`Ir para banner ${index + 1}`}
              style={{
                width: index === currentIndex ? '20px' : '8px',
                height: '8px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: index === currentIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                padding: 0,
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default BannerCarousel;
